import { Delete02Icon, TruckIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { api } from "@slapinc/slappos-backend/api";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import type { GenericId as Id } from "convex/values";
import { Layers, LayoutPanelTop } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Can } from "@/components/can";
import { PageContent } from "@/components/layout/page-content";
import { ActionHeaderButton } from "@/components/ui/action-header-button";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogMedia,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DisplaySelect } from "@/components/ui/display-select";
import { EmptyStateWithSkeleton } from "@/components/ui/empty-state-with-skeleton";
import { type Filter, Filters } from "@/components/ui/filters";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SearchInput } from "@/components/ui/updated-search-input";
import { PurchaseRequestsView } from "@/features/purchase-requests/_components/purchase-requests-view";
import { SupplierModal } from "@/features/suppliers/_components/supplier-modal";
import { SuppliersTable } from "@/features/suppliers/_components/suppliers-table";
import { useDebounce } from "@/hooks/use-debounce";
import { useFilters } from "@/hooks/use-filters";
import { useLocalStorageSettings } from "@/hooks/use-local-storage-settings";
import { useMe } from "@/hooks/use-me";
import { usePagination } from "@/hooks/use-pagination";
import { handleConvexError } from "@/lib/errors";
import type { SupplierInput } from "@/lib/schemas/supplier";
import { toast } from "@/lib/toast";
import type { Supplier } from "@/lib/types/supplier";

export const Route = createFileRoute("/(dashboard)/suppliers")({
	component: SuppliersPage,
});

const PAGE_SIZE = 10;

interface DisplaySettings {
	viewMode: "cards" | "rows";
	showHeaders: boolean;
	sortKey: string;
	columnVisibility: Record<string, boolean>;
}

const DEFAULT_SETTINGS: DisplaySettings = {
	viewMode: "rows",
	showHeaders: true,
	sortKey: "name",
	columnVisibility: {
		contact: true,
		status: true,
		licenseNumber: true,
	},
};

type SuppliersTab = "suppliers" | "purchase-requests";

function SuppliersPage() {
	// Get current user to find active organization
	const me = useMe();
	const orgId = me?.activeOrganizationId;

	// State
	const [tab, setTab] = useState<SuppliersTab>("suppliers");
	const [purchaseRequestsBranchId, setPurchaseRequestsBranchId] = useState<
		Id<"branches"> | undefined
	>();
	const [searchQuery, setSearchQuery] = useState("");
	const debouncedSearch = useDebounce(searchQuery.trim(), 300);

	const {
		settings: displaySettings,
		hasChanges,
		handleReset,
		handleSetDefault,
		updateSetting,
		updateColumnVisibility,
	} = useLocalStorageSettings<DisplaySettings>(
		"slappos_suppliers_display_settings",
		DEFAULT_SETTINGS,
	);

	const { resetPage } = usePagination(PAGE_SIZE);

	// Modal state
	const [supplierModalOpen, setSupplierModalOpen] = useState(false);
	const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(
		null,
	);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [supplierToDelete, setSupplierToDelete] = useState<Supplier | null>(
		null,
	);

	// Mutations
	const createSupplier = useMutation(api.features.suppliers.web.create);
	const updateSupplier = useMutation(api.features.suppliers.web.update);
	const deleteSupplier = useMutation(api.features.suppliers.web.remove);

	// Mutation loading states
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	// Build filters
	const supplierFilters: Filter[] = useMemo(
		() => [
			{
				key: "status",
				label: "Status",
				icon: <Layers className="size-4" />,
				options: [
					{ value: "active", label: "Active" },
					{ value: "inactive", label: "Inactive" },
				],
			},
		],
		[],
	);

	const filterState = useFilters({
		filters: supplierFilters,
		onFiltersChange: () => resetPage(),
	});

	// Get selected status from filters
	const selectedStatus = useMemo(() => {
		const statusFilter = filterState.activeFilters.find(
			(f) => f.key === "status",
		);
		if (!statusFilter) return undefined;
		const value = Array.isArray(statusFilter.value)
			? statusFilter.value[0]
			: statusFilter.value;
		return value as "active" | "inactive" | undefined;
	}, [filterState.activeFilters]);

	// Query suppliers - use search API when searching, list API otherwise
	const suppliersListQuery = useQuery(
		api.features.suppliers.web.list,
		orgId && !debouncedSearch ? { orgId, status: selectedStatus } : "skip",
	);

	const suppliersSearchQuery = useQuery(
		api.features.suppliers.web.search,
		orgId && debouncedSearch
			? { orgId, query: debouncedSearch, status: selectedStatus }
			: "skip",
	);

	const suppliers = useMemo(() => {
		const rawSuppliers = debouncedSearch
			? (suppliersSearchQuery ?? [])
			: (suppliersListQuery ?? []);

		const result = [...(rawSuppliers as Supplier[])];

		// Apply sorting
		return result.sort((a, b) => {
			if (displaySettings.sortKey === "name") {
				return a.name.localeCompare(b.name);
			}
			if (displaySettings.sortKey === "createdAt") {
				return (b._creationTime || 0) - (a._creationTime || 0);
			}
			return 0;
		});
	}, [
		debouncedSearch,
		suppliersListQuery,
		suppliersSearchQuery,
		displaySettings.sortKey,
	]);

	const isLoading = debouncedSearch
		? suppliersSearchQuery === undefined
		: suppliersListQuery === undefined;

	const suppliersToShow = suppliers;

	// Handlers
	const handleSearchChange = useCallback(
		(value: string) => {
			setSearchQuery(value);
			resetPage();
		},
		[resetPage],
	);

	const handleCreate = useCallback(() => {
		setSelectedSupplier(null);
		setSupplierModalOpen(true);
	}, []);

	const handleEdit = useCallback((supplier: Supplier) => {
		setSelectedSupplier(supplier);
		setSupplierModalOpen(true);
	}, []);

	const handleDelete = useCallback((supplier: Supplier) => {
		setSupplierToDelete(supplier);
		setDeleteDialogOpen(true);
	}, []);

	const confirmDelete = useCallback(async () => {
		if (!supplierToDelete || !orgId) return;

		setIsDeleting(true);
		try {
			await deleteSupplier({ orgId, supplierId: supplierToDelete._id });
			toast.success(<>Supplier deleted</>);
			setDeleteDialogOpen(false);
			setSupplierToDelete(null);
		} catch (error) {
			handleConvexError(error, "Failed to delete supplier");
		} finally {
			setIsDeleting(false);
		}
	}, [supplierToDelete, orgId, deleteSupplier]);

	const handleSupplierSubmit = useCallback(
		async (data: SupplierInput) => {
			if (!orgId) return;

			setIsSubmitting(true);
			try {
				const supplierData = {
					orgId,
					name: data.name,
					email: data.email || undefined,
					phone: data.phone || undefined,
					line: data.line || undefined,
					companyLegalName: data.companyLegalName || undefined,
					taxId: data.taxId || undefined,
					licenseNumber: data.licenseNumber || undefined,
					address: data.address || undefined,
					city: data.city || undefined,
					province: data.province || undefined,
					postalCode: data.postalCode || undefined,
					country: data.country || undefined,
					paymentTerms: data.paymentTerms || undefined,
					bankName: data.bankName || undefined,
					bankAccountName: data.bankAccountName || undefined,
					bankAccountNumber: data.bankAccountNumber || undefined,
					status: data.status,
					notes: data.notes || undefined,
				};

				if (selectedSupplier) {
					await updateSupplier({
						...supplierData,
						supplierId: selectedSupplier._id,
					});
					toast.success(<>Supplier updated</>);
				} else {
					await createSupplier(supplierData);
					toast.success(<>Supplier created</>);
				}
			} catch (error) {
				handleConvexError(
					error,
					selectedSupplier
						? "Failed to update supplier"
						: "Failed to create supplier",
				);
				throw error;
			} finally {
				setIsSubmitting(false);
			}
		},
		[orgId, selectedSupplier, createSupplier, updateSupplier],
	);

	return (
		<PageContent
			title="Suppliers"
			controls={
				<Can scope="suppliers.create">
					<ActionHeaderButton label="Add Supplier" onClick={handleCreate} />
				</Can>
			}
		>
			<div className="flex flex-1 flex-col gap-4 sm:gap-6">
				<Tabs
					value={tab}
					onValueChange={(value) => setTab(value as SuppliersTab)}
				>
					<TabsList>
						<TabsTrigger value="suppliers">Suppliers</TabsTrigger>
						<TabsTrigger value="purchase-requests">
							Purchase Requests
						</TabsTrigger>
					</TabsList>
				</Tabs>

				{tab === "purchase-requests" ? (
					<PurchaseRequestsView
						orgId={orgId}
						branchId={purchaseRequestsBranchId}
						onBranchChange={setPurchaseRequestsBranchId}
					/>
				) : (
					<>
						{/* Search and Filters */}
						{!isLoading &&
							(suppliers.length > 0 ||
								searchQuery ||
								filterState.activeFilters.length > 0) && (
								<>
									<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
										<div className="flex-1 sm:max-w-sm">
											<SearchInput
												placeholder="Search suppliers..."
												value={searchQuery}
												onChange={(e) => handleSearchChange(e.target.value)}
											/>
										</div>
										<div className="flex items-center gap-3">
											<Filters.Trigger
												filters={filterState.filters}
												activeFilters={filterState.activeFilters}
												onSelect={filterState.onSelect}
												onRemove={filterState.onRemove}
											/>
											<DisplaySelect
												viewMode={displaySettings.viewMode}
												onViewModeChange={(mode) =>
													updateSetting("viewMode", mode)
												}
												selectedSort={displaySettings.sortKey}
												onSortChange={(sort) => updateSetting("sortKey", sort)}
												sortOptions={[
													{ value: "name", label: "Supplier Name" },
													{ value: "createdAt", label: "Date Created" },
												]}
												options={[
													{
														key: "showHeaders",
														label: "Show table headers",
														checked: displaySettings.showHeaders,
														icon: <LayoutPanelTop className="size-4" />,
													},
												]}
												onOptionsChange={(key, checked) => {
													if (key === "showHeaders")
														updateSetting("showHeaders", checked);
												}}
												properties={[
													{
														key: "contact",
														label: "Contact Info",
														visible: displaySettings.columnVisibility.contact,
													},
													{
														key: "licenseNumber",
														label: "License No.",
														visible:
															displaySettings.columnVisibility.licenseNumber,
													},
													{
														key: "status",
														label: "Status",
														visible: displaySettings.columnVisibility.status,
													},
												]}
												onPropertiesChange={updateColumnVisibility}
												onReset={handleReset}
												onSetDefault={handleSetDefault}
												showFooter={hasChanges}
											/>
										</div>
									</div>

									{/* Active Filter Pills */}
									<Filters.List
										filters={filterState.filterListItems}
										onRemove={filterState.onRemove}
										onClear={filterState.onClear}
									/>
								</>
							)}

						{/* Suppliers Table or Empty State */}
						{!isLoading && suppliers.length === 0 ? (
							<div className="flex flex-1 flex-col justify-end pb-12">
								<EmptyStateWithSkeleton
									title="No suppliers found"
									description="Add suppliers to manage your inventory sources and track purchase orders"
									actionLabel="Add Supplier"
									onAction={handleCreate}
									icon={
										<HugeiconsIcon
											icon={TruckIcon}
											className="h-5 w-5 text-neutral-700"
											strokeWidth={2.2}
										/>
									}
								/>
							</div>
						) : (
							<SuppliersTable
								data={suppliersToShow}
								loading={isLoading}
								rowCount={suppliers.length}
								onEdit={handleEdit}
								onDelete={handleDelete}
								viewMode={displaySettings.viewMode}
								showHeaders={displaySettings.showHeaders}
								columnVisibility={displaySettings.columnVisibility}
								showPagination={false}
							/>
						)}
					</>
				)}
			</div>

			{/* Supplier Modal (Create/Edit) */}
			<SupplierModal
				open={supplierModalOpen}
				onOpenChange={setSupplierModalOpen}
				supplier={selectedSupplier}
				orgId={orgId}
				onSubmit={handleSupplierSubmit}
				isSubmitting={isSubmitting}
			/>

			{/* Delete Confirmation Dialog */}
			<AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogMedia className="bg-red-50 text-red-600">
							<HugeiconsIcon icon={Delete02Icon} strokeWidth={2} />
						</AlertDialogMedia>
						<AlertDialogTitle>Delete supplier?</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to delete{" "}
							<span className="font-medium text-neutral-900">
								"{supplierToDelete?.name}"?
							</span>{" "}
							This will remove all associated documents and contact info. This
							action cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={confirmDelete}
							disabled={isDeleting}
							className="rounded-xl px-4"
						>
							{isDeleting ? "Deleting..." : "Delete Supplier"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</PageContent>
	);
}
